/**
 * Utility functions for handling subscription plans consistently
 * Supports both Virtual Assistant and Virtual Host products
 */

// Virtual Assistant plans
export type VirtualAssistantPlan = 'Trial' | 'Yummy' | 'Delicious' | 'Gourmet';

// Virtual Host plans
export type VirtualHostPlan = 'Basic Virtual Host' | 'Standard Virtual Host' | 'Premium Virtual Host';

// All plan types
export type PlanName = VirtualAssistantPlan | VirtualHostPlan;

export type PlanNameLower = 'trial' | 'yummy' | 'delicious' | 'gourmet'
  | 'basic_virtual_host' | 'standard_virtual_host' | 'premium_virtual_host';

/**
 * Product types
 */
export type ProductType = 'virtual_assistant' | 'virtual_host' | 'unknown';

/**
 * Determine product type from plan name
 */
export function getProductType(planName: string | undefined | null): ProductType {
  if (!planName) return 'unknown';

  const normalized = planName.toLowerCase().trim();

  if (normalized.includes('virtual_host') || normalized.includes('virtual host')) {
    return 'virtual_host';
  }

  if (['trial', 'yummy', 'delicious', 'gourmet'].includes(normalized)) {
    return 'virtual_assistant';
  }

  return 'unknown';
}

/**
 * Normalize plan name to consistent format (Title Case)
 */
export function normalizePlanName(plan: string | undefined | null): PlanName {
  if (!plan) return 'Trial';

  const normalized = plan.toLowerCase().trim();

  // Virtual Host plans (check first since they're more specific)
  if (normalized === 'basic_virtual_host' || normalized === 'basic virtual host') {
    return 'Basic Virtual Host';
  }
  if (normalized === 'standard_virtual_host' || normalized === 'standard virtual host') {
    return 'Standard Virtual Host';
  }
  if (normalized === 'premium_virtual_host' || normalized === 'premium virtual host') {
    return 'Premium Virtual Host';
  }

  // Virtual Assistant plans
  switch (normalized) {
    case 'trial':
      return 'Trial';
    case 'yummy':
      return 'Yummy';
    case 'delicious':
      return 'Delicious';
    case 'gourmet':
      return 'Gourmet';
    default:
      console.warn(`Unknown plan name: ${plan}, defaulting to Trial`);
      return 'Trial';
  }
}

/**
 * Check if a plan has AI image generation access
 * All plans (Virtual Assistant and Virtual Host) have AI image generation
 */
export function planHasAIImages(plan: string | undefined | null): boolean {
  const normalized = normalizePlanName(plan);

  // Virtual Host plans have AI images (150/year default)
  if (getProductType(plan) === 'virtual_host') {
    return true;
  }

  // Virtual Assistant plans have AI images
  return ['Yummy', 'Delicious', 'Gourmet', 'Trial'].includes(normalized);
}

/**
 * Get AI image limit for a plan (yearly quotas)
 * Note: These are YEARLY limits, not monthly
 * Virtual Host plans get 150 AI images/year
 */
export function getPlanImageLimit(plan: string | undefined | null): number {
  // Virtual Host plans get 150 AI images/year (same as Delicious/Trial)
  if (getProductType(plan) === 'virtual_host') {
    return 150;
  }

  const normalized = normalizePlanName(plan);

  switch (normalized) {
    case 'Gourmet':
      return 300; // 300 per year
    case 'Delicious':
      return 150; // 150 per year
    case 'Trial':
      return 150; // Trial gets Delicious level access
    case 'Yummy':
      return 50; // 50 per year
    default:
      return 0;
  }
}

/**
 * Check if plan has specific feature
 */
export function planHasFeature(plan: string | undefined | null, feature: string): boolean {
  const normalized = normalizePlanName(plan);

  const features: Record<PlanName, string[]> = {
    // Virtual Assistant plans
    'Trial': ['menu_assistant', 'ai_images', 'pos_integration', 'analytics', 'auto_promotions', 'advanced_insights'],
    'Yummy': ['menu_assistant', 'ai_images'],
    'Delicious': ['menu_assistant', 'ai_images', 'pos_integration', 'analytics'],
    'Gourmet': ['menu_assistant', 'ai_images', 'pos_integration', 'analytics', 'auto_promotions', 'advanced_insights'],

    // Virtual Host plans (phone ordering + AI images)
    'Basic Virtual Host': ['phone_ordering', 'call_analytics', 'order_queue', 'sms_notifications', 'ai_images'],
    'Standard Virtual Host': ['phone_ordering', 'call_analytics', 'order_queue', 'sms_notifications', 'advanced_analytics', 'ai_images'],
    'Premium Virtual Host': ['phone_ordering', 'call_analytics', 'order_queue', 'sms_notifications', 'advanced_analytics', 'priority_support', 'ai_images']
  };

  return features[normalized]?.includes(feature) || false;
}